import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  ChevronDown,
  Compass,
  Home,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Search,
  Settings,
  Trophy,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { label: "Home", path: "/", icon: Home },
  { label: "Raise Issue", path: "/raise-issue", icon: Compass },
  { label: "Issues", path: "/issues", icon: Compass },
  { label: "Explore", path: "/explore-candidates", icon: Compass },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { isAuthenticated, logout, user, userPrimaryRoute } = useAuth();

  useEffect(() => {
    setProfileMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!search.trim()) return;
    navigate(`/state/${encodeURIComponent(search.trim())}`);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    setMobileOpen(false);
    navigate("/login");
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive =
      location.pathname === item.path ||
      (item.path !== "/" && location.pathname.startsWith(item.path));

    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`group relative inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${
          isActive ? "text-orange-600" : "text-slate-700 hover:text-orange-600"
        } ${mobile ? "w-full justify-start" : ""}`}
      >
        <item.icon className="h-4 w-4" />
        <span>{item.label}</span>
        {!mobile && isActive && (
          <motion.span
            layoutId="nav-underline"
            className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-orange-500 shadow-[0_0_14px_rgba(249,115,22,0.55)]"
          />
        )}
      </Link>
    );
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 px-3 py-3 sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 rounded-[1.75rem] border border-white/60 bg-white/62 px-4 py-3 shadow-[0_10px_40px_rgba(15,23,42,0.08)] backdrop-blur-2xl">
          
          <div className="flex items-center gap-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate("/")}
              className="group flex items-center gap-3"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_0_24px_rgba(249,115,22,0.22)]">
                <img src="/logo.jpg" alt="JanShakti" className="h-10 w-10 rounded-full object-cover" />
              </span>
              <span className="hidden sm:block text-left">
                <span className="block text-base font-black tracking-tight text-slate-900">JanShakti</span>
                <span className="block text-[11px] uppercase tracking-[0.24em] text-slate-500">Citizen Power</span>
              </span>
            </motion.button>

            <nav className="hidden items-center gap-1 lg:flex">
              {navItems.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <form onSubmit={handleSearchSubmit} className="hidden xl:flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-4 py-2 shadow-sm">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search state..."
                  className="w-32 bg-transparent text-sm outline-none placeholder:text-slate-400 focus:w-44 transition-all"
                />
              </form>
            )}

            {!isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Link to="/login" className="px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50 rounded-full transition">
                  Login
                </Link>
                <Link to="/register" className="rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200/70 hover:scale-[1.03] transition">
                  Register
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="hidden items-center gap-4 border-r border-slate-200 pr-4 md:flex">
                  <Link to="/my-impact" className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-orange-600 transition">
                    <Trophy className="h-4 w-4 text-orange-500" />
                    <span>My Impact</span>
                  </Link>
                  <Link to="/my-constituency" className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-orange-600 transition">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <span>My Constituency</span>
                  </Link>
                </div>

                <button className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100 transition">
                  <Bell className="h-5 w-5" />
                  <span className="absolute right-1.5 top-1.5 flex h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
                </button>

                <div className="relative">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/50 p-1 pr-3 transition hover:bg-white"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-green-500 text-xs font-bold text-white uppercase">
                      {user?.name?.charAt(0) || "N"}
                    </div>
                    <span className="hidden text-sm font-bold text-slate-700 sm:block">
                      {user?.name || "Navin Kumar"}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {profileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute right-0 mt-3 w-56 origin-top-right rounded-3xl border border-white/70 bg-white/95 p-2 shadow-2xl backdrop-blur-xl ring-1 ring-black/5"
                      >
                        <Link to={userPrimaryRoute} onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
                          <LayoutDashboard className="h-4 w-4 text-slate-400" /> Dashboard
                        </Link>
                        <Link to="/my-issues" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
                          <Compass className="h-4 w-4 text-slate-400" /> My Issues
                        </Link>
                        <Link to="/edit-profile" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
                          <Settings className="h-4 w-4 text-slate-400" /> Edit Profile
                        </Link>
                        <hr className="my-2 border-slate-100" />
                        <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50 transition">
                          <LogOut className="h-4 w-4" /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white/80 text-slate-700 md:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            className="fixed inset-0 z-[60] bg-white md:hidden"
          >
            <div className="flex h-full flex-col p-6">
              <div className="flex items-center justify-between mb-8">
                <span className="text-xl font-black text-slate-900">JanShakti</span>
                <button onClick={() => setMobileOpen(false)} className="rounded-full bg-slate-100 p-2"><X /></button>
              </div>

              <form onSubmit={handleSearchSubmit} className="mb-6 flex items-center gap-2 rounded-2xl border bg-slate-50 px-4 py-3">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search state..."
                  className="w-full bg-transparent text-sm outline-none"
                />
              </form>

              <div className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <NavLink key={item.path} item={item} mobile />
                ))}
              </div>

              <div className="mt-auto space-y-4">
                {isAuthenticated ? (
                  <div className="grid gap-3">
                    <Link to="/my-impact" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl border p-4 text-slate-700">
                      <Trophy className="h-5 w-5 text-orange-500" />
                      <span className="font-bold">My Impact</span>
                    </Link>
                    <Link to="/my-constituency" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl border p-4 text-slate-700">
                      <MapPin className="h-5 w-5 text-green-600" />
                      <span className="font-bold">My Constituency</span>
                    </Link>
                    <button onClick={handleLogout} className="w-full rounded-2xl bg-rose-50 py-4 font-bold text-rose-600">
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="rounded-2xl border border-orange-200 py-4 text-center font-bold text-orange-600">Login</Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)} className="rounded-2xl bg-orange-500 py-4 text-center font-bold text-white">Register</Link>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
